import React, { useEffect, useState } from "react";
import { HelionButton } from "@components/ui/HelionButton";
import { Card, SectionCard, chipClass, type ChipTone, cardStyle } from "@components/cockpit";
import { RISK_METHODOLOGY } from "./riskMethodologyContent";
import { metricBadges, type MethodologyContent } from "./methodologyTypes";

/** One labelled body line in a metric card ("Tells you: …", "Moves it: …"). */
const MetricField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="font-semibold" style={{ color: "var(--text-color)" }}>
      {label}:
    </span>{" "}
    {value}
  </div>
);

/**
 * RiskMethodologyModal — the "how to read this board" reference for the Risk
 * Command Center, modeled on the Economics dashboard methodology modal.
 *
 * Copy lives in riskMethodologyContent.ts (grounded against the HelionBackend
 * risk_* modules + RiskDashboard.tsx, then adversarially fact-checked). This
 * component is presentation only — it renders that content in four tabs and
 * never fetches, computes, or mutates anything.
 */

type TabKey = "framework" | "metrics" | "playbook" | "operations";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "framework", label: "Framework" },
  { key: "metrics", label: "Metric Guide" },
  { key: "playbook", label: "How to Use" },
  { key: "operations", label: "Data & Governance" },
];

const EYEBROW = "text-[10px] uppercase tracking-[0.16em] opacity-60";
const SOFT_CLASS = "rounded-xl p-3 border border-[var(--border-color)]";
const SOFT_STYLE: React.CSSProperties = { backgroundColor: "var(--bg-secondary)" };

/** Color a legend chip to match the dashboard tone the meaning text describes. */
function legendTone(meaning: string): ChipTone {
  const m = meaning.toLowerCase();
  if (/\bgreen\b/.test(m) || /\bemerald\b/.test(m)) return "positive";
  if (/\bred\b/.test(m)) return "danger";           // word-boundary: don't match "c-red-it", "hund-red", etc.
  if (/\bamber\b/.test(m)) return "warning";
  if (/\belevated\b/.test(m)) return "elevated";
  if (/\bblue\b/.test(m)) return "info";
  return "neutral";
}

/** A subtle semantic tone for a metric's caveat pill. */
function tagTone(tag: string): ChipTone {
  const t = tag.toLowerCase();
  if (/heuristic|not calibrated|not oos|unverified|directional|approx/.test(t)) return "warning";
  if (/review-only|display-only|informational/.test(t)) return "info";
  return "neutral";
}

export const RiskMethodologyModal: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Methodology copy to render. Defaults to the Risk Command Center content; the Signals
   *  dashboard passes its own (same shape) so this one component serves both boards. */
  content?: MethodologyContent;
  titleId?: string;
}> = ({ open, onClose, content = RISK_METHODOLOGY, titleId = "risk-methodology-title" }) => {
  const [tab, setTab] = useState<TabKey>("framework");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const c = content;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden p-3 sm:p-6"
      style={{ backgroundColor: "rgba(8, 15, 30, 0.72)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl sm:max-h-[calc(100vh-3rem)]"
        style={cardStyle}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex flex-shrink-0 items-start justify-between gap-4 border-b p-5"
          style={{ borderColor: "var(--border-color)" }}
        >
          <div>
            <div className={`${EYEBROW} mb-2`}>{c.intro.eyebrow}</div>
            <h2 id={titleId} className="text-xl font-semibold leading-none">
              {c.intro.title}
            </h2>
            <p
              className="mt-2 max-w-3xl text-[13px] leading-6"
              style={{ color: "var(--text-muted)" }}
            >
              {c.intro.subtitle}
            </p>
          </div>
          <HelionButton size="sm" onClick={onClose}>
            Close
          </HelionButton>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-10 space-y-5">
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((t) => {
              const active = t.key === tab;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
                    active
                      ? "border-[var(--accent-info)] text-[var(--accent-info)]"
                      : "border-[var(--border-color)] opacity-75 hover:opacity-100"
                  }`}
                  style={
                    active
                      ? { backgroundColor: "var(--accent-info-soft)" }
                      : SOFT_STYLE
                  }
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Framework */}
          {tab === "framework" ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card className="xl:col-span-2">
                  <h3 className="text-base font-semibold">Purpose</h3>
                  <p
                    className="mt-2 text-[13px] leading-6"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {c.intro.purpose}
                  </p>
                  <div className={`${SOFT_CLASS} mt-4`} style={SOFT_STYLE}>
                    <div className={`${EYEBROW} mb-1`}>Core rule</div>
                    <div className="text-[13px] leading-6" style={{ color: "var(--text-muted)" }}>
                      {c.intro.coreRule}
                    </div>
                  </div>
                </Card>

                <Card>
                  <h3 className="text-base font-semibold">Check these first</h3>
                  <div className="mt-3 space-y-2.5">
                    {c.earlyReads.map((item) => (
                      <div key={item.title} className={SOFT_CLASS} style={SOFT_STYLE}>
                        <div className="font-semibold text-[13px] leading-5">{item.title}</div>
                        <p
                          className="mt-1 text-[12px] leading-5"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {item.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <div>
                <div className={`${EYEBROW} mb-2`}>Scales &amp; tones used on the board</div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {c.toneLegends.map((legend) => (
                    <div key={legend.group} className="rounded-xl p-4" style={SOFT_STYLE}>
                      <h4 className="font-semibold text-[13px]">{legend.group}</h4>
                      <div className="mt-3 space-y-2.5">
                        {legend.items.map((item) => (
                          <div key={item.label} className="flex items-start gap-2">
                            <span
                              className={`${chipClass(legendTone(item.meaning))} shrink-0 mt-0.5`}
                            >
                              {item.label}
                            </span>
                            <span
                              className="text-[12px] leading-5"
                              style={{ color: "var(--text-muted)" }}
                            >
                              {item.meaning}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {/* Metric Guide */}
          {tab === "metrics" ? (
            <div className="space-y-5">
              <div className={SOFT_CLASS} style={SOFT_STYLE}>
                <h3 className="text-base font-semibold">Metric guide</h3>
                <p
                  className="mt-2 text-[13px] leading-6"
                  style={{ color: "var(--text-muted)" }}
                >
                  Every tile, grouped by the panel it lives in, with the same anatomy. Three badges orient you
                  first — <span className="font-medium">Subject</span> (our book · the market · a forward scenario ·
                  a reconciliation), <span className="font-medium">Time basis</span> (live · close-confirmed · daily
                  snapshot · backtest), and <span className="font-medium">Role</span> (drives sizing/the cap ·
                  situational · informational · official) — then what it is, what it tells you, what moves it, how
                  to read it, what it is <span className="font-medium">not</span>, what to read alongside, and the
                  source. “Show the math” expands the formula. Stress / forward figures are modeled and directional
                  — not statistical VaR and not calibrated probabilities.
                </p>
              </div>

              {c.panels.map((panel) => (
                <SectionCard key={panel.name} title={panel.name} subtitle={panel.summary}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {panel.metrics.map((metric) => {
                      const badges = metricBadges(metric);
                      const tellsYou = metric.tellsYou ?? metric.why;
                      const computed = metric.computed ?? metric.how;
                      const limits = metric.limits ?? metric.tag;
                      return (
                        <div
                          key={`${panel.name}-${metric.label}`}
                          className={SOFT_CLASS}
                          style={SOFT_STYLE}
                        >
                          <div className="min-w-0">
                            <div className={EYEBROW}>{metric.key}</div>
                            <div className="font-semibold text-[13px] leading-5">{metric.label}</div>
                          </div>

                          {/* Orienting badges: Subject · Time basis · Role */}
                          {badges.length ? (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {badges.map((b) => (
                                <span
                                  key={b.label}
                                  className={`${chipClass(b.tone)} text-[10px]`}
                                  title={b.hint}
                                >
                                  {b.label}
                                </span>
                              ))}
                            </div>
                          ) : null}

                          <div
                            className="mt-2.5 text-[12px] leading-5 space-y-1.5"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {metric.whatItIs ? (
                              <div className="font-medium" style={{ color: "var(--text-color)" }}>
                                {metric.whatItIs}
                              </div>
                            ) : null}
                            {tellsYou ? <MetricField label="Tells you" value={tellsYou} /> : null}
                            {metric.movesIt ? <MetricField label="Moves it" value={metric.movesIt} /> : null}
                            {metric.readIt ? <MetricField label="Read it" value={metric.readIt} /> : null}
                            {metric.typicalRange ? <MetricField label="Typical range" value={metric.typicalRange} /> : null}
                            {metric.gauge ? <MetricField label="Where it sits" value={metric.gauge} /> : null}
                            {limits ? (
                              <div>
                                <span
                                  className={`${chipClass(tagTone(limits))} text-[10px] mr-1.5`}
                                >
                                  Not
                                </span>
                                {limits}
                              </div>
                            ) : null}
                            {metric.readWith ? <MetricField label="Read with" value={metric.readWith} /> : null}
                            {metric.source ? <MetricField label="Source" value={metric.source} /> : null}
                            {computed ? (
                              <details className="mt-1">
                                <summary
                                  className="cursor-pointer text-[11px] font-semibold select-none"
                                  style={{ color: "var(--accent-info)" }}
                                >
                                  Show the math
                                </summary>
                                <div className="mt-1">{computed}</div>
                              </details>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              ))}
            </div>
          ) : null}

          {/* How to Use */}
          {tab === "playbook" ? (
            <div className="space-y-5">
              <div className={SOFT_CLASS} style={SOFT_STYLE}>
                <h3 className="text-base font-semibold">Interpretation playbook</h3>
                <p
                  className="mt-2 text-[13px] leading-6"
                  style={{ color: "var(--text-muted)" }}
                >
                  The board is strongest read as a whole. These are the reference patterns for moving from
                  model output to portfolio judgment during CIO review.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {c.playbook.map((item) => (
                  <div key={item.title} className="rounded-xl p-4" style={SOFT_STYLE}>
                    <h4 className="font-semibold text-[13px] leading-5">{item.title}</h4>
                    <p
                      className="mt-2 text-[12px] leading-6"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Data & Governance */}
          {tab === "operations" ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card>
                  <h3 className="text-base font-semibold">Data sources</h3>
                  <div className="mt-3 space-y-2.5">
                    {c.operations.dataSources.map((item) => (
                      <div key={item.label} className={SOFT_CLASS} style={SOFT_STYLE}>
                        <div className="font-semibold text-[12px] leading-5">{item.label}</div>
                        <p
                          className="mt-1 text-[12px] leading-5"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {item.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card>
                  <h3 className="text-base font-semibold">Freshness &amp; fallback</h3>
                  <div className="mt-3 space-y-2.5">
                    {c.operations.freshness.map((item) => (
                      <div key={item.label} className={SOFT_CLASS} style={SOFT_STYLE}>
                        <div className="font-semibold text-[12px] leading-5">{item.label}</div>
                        <p
                          className="mt-1 text-[12px] leading-5"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {item.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <Card>
                <h3 className="text-base font-semibold">Governance &amp; boundaries</h3>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {c.operations.governance.map((item) => (
                    <div key={item.label} className={SOFT_CLASS} style={SOFT_STYLE}>
                      <div className={`${EYEBROW} mb-1`}>{item.label}</div>
                      <div className="text-[12px] leading-6" style={{ color: "var(--text-muted)" }}>
                        {item.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <div
                className="rounded-xl p-4"
                style={{ ...SOFT_STYLE, border: "1px solid var(--accent-gold)" }}
              >
                <div className="font-semibold text-[13px]">Governance note</div>
                <p
                  className="mt-2 text-[12px] leading-6"
                  style={{ color: "var(--text-muted)" }}
                >
                  {c.operations.governanceNote}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RiskMethodologyModal;
